import { Stack } from "@chakra-ui/react"
import { useNavigate } from "@tanstack/react-router"
import { Form, Formik, type FormikHelpers, type FormikProps } from "formik"
import type React from "react"
import { useEffect, useState } from "react"
import {
  type ASPServiceForm as ASPServiceFormType,
  type CartItemCreate,
  CartService,
  type Item,
  ItemsService,
  type PricerRequest,
  type PricerResponse,
} from "../client"
import ASPServiceForm, {
  initialValues as aspInitialValues,
  validationSchema as aspValidationSchema,
} from "./ASPServiceForm"
import PCButton from "./PCButton"

interface CustomizerProps {
  item_code: { code: string }
  price: number | null
}

type ServiceForm = ASPServiceFormType

// Define getInitialValues function
const getInitialValues = (code: string): ServiceForm => {
  switch (code) {
    case "ASP":
      return aspInitialValues
    default:
      throw new Error("Not implemented")
  }
}
const getValidationSchema = (code: string): any => {
  switch (code) {
    case "ASP":
      return aspValidationSchema
    default:
      throw new Error("Not implemented")
  }
}

const Customizer: React.FC<CustomizerProps> = ({ item_code, price }) => {
  type ServiceForm = ASPServiceFormType
  const navigate = useNavigate()
  const [currentPrice, setCurrentPrice] = useState<number | null>(price)
  const [itemId, setItemId] = useState<string | null>(null) // State for item ID
  const fetchPrice = async (values: ServiceForm) => {
    try {
      const request: PricerRequest = { configuration: values }
      const response: PricerResponse = await CartService.getPrice({
        requestBody: request,
      })
      setCurrentPrice(Number.parseFloat(response.price))
    } catch (error) {
      console.error("Error fetching price:", error)
    }
  }
  const fetchItem = async () => {
    try {
      const items = await ItemsService.readItems()
      const foundItem = items?.data.find(
        (item: Item) => item_code.code === item.code,
      )
      if (foundItem) {
        setItemId(foundItem.id ?? null)
      }
    } catch (error) {
      console.error("Error fetching item:", error)
    }
  }

  useEffect(() => {
    fetchItem()
  })

  const handleSubmit = async (
    values: ServiceForm,
    { setSubmitting }: FormikHelpers<ServiceForm>,
  ) => {
    let configuration: ServiceForm
    switch (item_code.code) {
      case "ASP":
        configuration = { ...values } as ASPServiceFormType
        break
      default:
        throw new Error("Unknown item type")
    }

    const cartItem: CartItemCreate = {
      item_id: itemId || "",
      configuration: configuration,
    }

    try {
      await CartService.addToCart({ requestBody: cartItem })

      window.parent.postMessage({ navigateTo: "cart" }, "*")
      navigate({ to: "/cart" })
    } catch (error) {
      console.error("There was an error submitting the form:", error)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Formik
      initialValues={getInitialValues(item_code.code)}
      validationSchema={getValidationSchema(item_code.code)}
      validateOnBlur={true}
      validateOnChange={true}
      validateOnMount={true}
      onSubmit={handleSubmit}
    >
      {({ values }: FormikProps<any>) => {
        useEffect(() => {
          fetchPrice(values)
        }, [values])

        return (
          <Form>
            <Stack spacing={4} onChange={() => fetchPrice(values)}>
              {item_code.code === "ASP" ? (
                <ASPServiceForm />
              ) : (
                <div>Unknown item type</div>
              )}
              {currentPrice !== null && <div>Price: ${currentPrice}</div>}
              <PCButton name="submit" type="submit" variant="emphasis">
                Add to Cart
              </PCButton>
            </Stack>
          </Form>
        )
      }}
    </Formik>
  )
}

export default Customizer
